import { useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Mail, Lock, ArrowLeft } from 'lucide-react'
import CaptchaCheckbox from '../components/CaptchaCheckbox'
import { Button, Input } from '../components/ui'
import { Capacitor } from '@capacitor/core'
import { isOurApp } from '../lib/platform'
import { canSignInNatively, signInWithNativePicker } from '../lib/googleSignIn'
import { Browser } from '@capacitor/browser'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  // Set when the server rejects sign-in because the address is unconfirmed
  const [unverifiedEmail, setUnverifiedEmail] = useState('')
  const [resending, setResending] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaAnswer, setCaptchaAnswer] = useState<number | null>(null)
  const [captchaResetKey, setCaptchaResetKey] = useState(0)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setAuthData, isAuthenticated } = useAuth()

  /**
   * Already signed in, in the app? Then this page is a dead end.
   *
   * The app cold-starts on '/' and something has twice managed to route that
   * to here with a perfectly good session behind it — first an effect in
   * App.tsx that never checked, and before that a landing page that could not.
   * Each time it read as being logged out, because a login screen is
   * indistinguishable from one.
   *
   * A guard on the page itself does not care how it was reached, which is the
   * point: it closes the whole class rather than the two routes that caused it.
   * App only — a signed-in visitor opening /login on the website is their own
   * business and that behaviour is left alone.
   *
   * No check on `loading`: `isAuthenticated` is `token && user`, so it cannot
   * be true until the session has been resolved. AppLanding does need one,
   * because it redirects signed-*out* users too and would otherwise act on the
   * first render, when everyone looks signed out.
   */
  if (isOurApp() && isAuthenticated) {
    return <Navigate to={searchParams.get('returnUrl') || '/learning'} replace />
  }

  const handleCaptchaVerified = (token: string, answer: number) => {
    setCaptchaToken(token)
    setCaptchaAnswer(answer)
  }

  const handleCaptchaExpired = () => {
    setCaptchaToken('')
    setCaptchaAnswer(null)
  }

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return
    setResending(true)
    try {
      await authAPI.resendVerification(unverifiedEmail)
      toast.success('Confirmation email sent — check your inbox.')
    } catch {
      toast.error('Could not send the email right now. Please try again shortly.')
    } finally {
      setResending(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!captchaToken || captchaAnswer === null) {
      toast.error('Please complete the CAPTCHA verification')
      return
    }

    setLoading(true)

    try {
      const response = await authAPI.login(email, password, captchaToken, captchaAnswer)
      const tokens = response.data.tokens || {
        access: response.data.access,
        refresh: response.data.refresh,
      }
      setAuthData(tokens.access, response.data.user, tokens.refresh)
      toast.success('Welcome back!')
      // Redirect to return URL — check query param first, then sessionStorage backup
      const returnUrl = searchParams.get('returnUrl') || sessionStorage.getItem('loginReturnUrl')
      // Consumed on use — it was already doing this; the leak was that logout
      // left it behind, so it survived into somebody else's session.
      sessionStorage.removeItem('loginReturnUrl')
      window.location.href = returnUrl || '/learning'
    } catch (error: any) {
      console.error('Login error:', error.response?.data)
      // Reset CAPTCHA on failure so user can re-verify
      handleCaptchaExpired()
      setCaptchaResetKey(k => k + 1)
      if (error.response?.data?.code === 'email_not_verified') {
        // Not a credentials problem — show an inline resend affordance
        // instead of a toast the user can't act on.
        setUnverifiedEmail(error.response.data.email || email)
        toast.error('Please confirm your email address first.')
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error)
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error('Invalid credentials. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    const clientId = '1018587300192-m0n93uesm6v33bahs57tatg52v3lurah.apps.googleusercontent.com'
    // A separate path for the app.
    //
    // The app has to claim its callback URL so Android routes it back after
    // the Custom Tab — but a claimed link belongs to the app *everywhere*, so
    // claiming /auth/callback also dragged the website's own sign-in, in
    // Chrome on the same phone, into the app. Two paths, one claimed.
    const callbackPath = isOurApp() ? '/auth/app-callback' : '/auth/callback'
    const redirectUri = encodeURIComponent(`${window.location.origin}${callbackPath}`)
    const scope = encodeURIComponent('openid email profile')
    const returnUrl = searchParams.get('returnUrl') || '/learning'
    const state = btoa(JSON.stringify({ mode: 'login', returnUrl }))
    // `select_account` as well as `consent`: with consent alone Google reuses
    // whoever is already signed in to the browser, so choosing the wrong
    // account once leaves no way to choose a different one — the same dead end
    // the native picker had. `consent` stays because access_type=offline only
    // returns a refresh token when consent is re-granted.
    const prompt = encodeURIComponent('select_account consent')
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=${prompt}&state=${state}`

    // Android's own account picker first: a sheet over the app rather than a
    // browser. Falls through to the Custom Tab when it cannot run — no Play
    // services, certificate not registered, plugin missing.
    if (canSignInNatively()) {
      const native = await signInWithNativePicker()

      // Dismissing the sheet is an answer. Opening a browser full of Google
      // accounts instead of accepting it is how this went wrong before.
      if (native.status === 'cancelled') return

      if (native.status === 'signed-in') {
        try {
          const { data } = await authAPI.googleNativeSignIn(native.idToken, 'login')
          if (data?.tokens) {
            setAuthData(data.tokens.access, data.user, data.tokens.refresh)
            toast.success('Welcome back!')
            const back = searchParams.get('returnUrl')
              || sessionStorage.getItem('loginReturnUrl')
            sessionStorage.removeItem('loginReturnUrl')
            window.location.href = back || '/learning'
            return
          }
        } catch (problem: any) {
          toast.error(problem?.response?.data?.error
            ?? 'Google could not verify that account.')
          return
        }
      } else {
        // Say why the browser is about to open. A second account list that
        // arrives unannounced looks like the app is broken — it is the same
        // sign-in, one rung down, and the reason belongs on screen.
        toast(`Opening browser sign-in instead — ${native.reason}.`,
          { icon: 'ℹ️', duration: 6000 })
      }
    }

    if (Capacitor.isNativePlatform()) {
      // Chrome Custom Tab. The callback URL triggers the intent filter, which
      // routes back into the app — see /.well-known/assetlinks.json.
      await Browser.open({ url: authUrl })
    } else {
      window.location.href = authUrl
    }
  }


  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4">
      {/* Single restrained purple glow — no multi-blob slop */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-purple-600/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Card */}
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900/70 backdrop-blur-sm p-6 sm:p-8 shadow-card">
          {/* Logo & Title */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <img src="/logo/ccis-logo.png" alt="CCIS CodeHub" className="h-14 w-14 sm:h-16 sm:w-16" />
            </div>
            <h1 className="text-2xl font-bold text-white">Welcome back</h1>
            <p className="text-neutral-400 text-sm mt-1">Sign in to continue learning</p>
            {searchParams.get('reason') === 'expired' && (
              <p role="status" className="mx-auto mt-3 max-w-xs rounded-lg bg-amber-500/10
                px-3 py-2 text-xs leading-relaxed text-amber-200">
                Your session ended, so you were signed out. Sign in and you will
                go straight back to what you were doing.
              </p>
            )}
          </div>

          {/* Form */}
          {/* Sign-in blocked because the address is unconfirmed — give the
              user a way out instead of a dead-end error. */}
          {unverifiedEmail && (
            <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
              <p className="text-sm font-medium text-amber-200">Confirm your email to continue</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-200/70">
                We sent a link to <span className="font-medium">{unverifiedEmail}</span>.
                Click it to activate your account, then sign in.
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                className="mt-3 rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs font-semibold text-amber-100 transition hover:bg-amber-500/30 disabled:opacity-50"
              >
                {resending ? 'Sending…' : 'Resend confirmation email'}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={<Mail className="w-4 h-4" />}
              placeholder="your.email@ssct.edu.ph"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon={<Lock className="w-4 h-4" />}
              placeholder="••••••••"
              required
            />

            <CaptchaCheckbox
              onVerified={handleCaptchaVerified}
              onExpired={handleCaptchaExpired}
              resetKey={captchaResetKey}
            />

            <Button type="submit" fullWidth size="lg" loading={loading} disabled={loading || !captchaToken}>
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-800" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 text-xs text-neutral-500 bg-neutral-900">or continue with</span>
            </div>
          </div>

          {/* Google Button - Circle Icon */}
          <div className="flex justify-center">
            <button
              onClick={handleGoogleLogin}
              className="w-12 h-12 bg-neutral-100 hover:bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 ring-1 ring-gray-200"
              title="Continue with Google"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </button>
          </div>

          {/* Footer Links */}
          <div className="mt-5 text-center space-y-2">
            <p className="text-sm text-neutral-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
                Sign up
              </Link>
            </p>
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors">
              <ArrowLeft className="w-3 h-3" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
